import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Button, Form, Alert, ListGroup, Badge, Spinner, Tabs, Tab, Modal, Accordion } from 'react-bootstrap'
import { useProfile } from '../contexts/ProfileContext'
import profileService from '../services/profileService'
import trustListService from '../services/trustListService'

function TrustLists() {
  const { activeProfile } = useProfile()

  // My Lists state
  const [myLists, setMyLists] = useState([])
  const [myCategories, setMyCategories] = useState([])
  const [loadingMyLists, setLoadingMyLists] = useState(false)

  // Available Lists state
  const [trustedProfiles, setTrustedProfiles] = useState([])
  const [selectedTrustedProfile, setSelectedTrustedProfile] = useState(null)
  const [availableLists, setAvailableLists] = useState([])
  const [loadingTrustedProfiles, setLoadingTrustedProfiles] = useState(false)
  const [loadingAvailableLists, setLoadingAvailableLists] = useState(false)

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAddItemsModal, setShowAddItemsModal] = useState(false)
  const [showAdoptModal, setShowAdoptModal] = useState(false)
  const [editingList, setEditingList] = useState(null)
  const [adoptingList, setAdoptingList] = useState(null)

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_public: true,
    category_ids: []
  })
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([])
  const [adoptOptions, setAdoptOptions] = useState({
    adopt_all: true,
    item_ids: [],
    trust_override: null,
    duplicate_handling: 'skip'
  })

  // UI state
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  // Load data when profile changes
  useEffect(() => {
    if (activeProfile) {
      loadMyLists()
      loadMyCategories()
      loadTrustedProfiles()
    }
  }, [activeProfile])

  // Load available lists when trusted profile changes
  useEffect(() => {
    if (selectedTrustedProfile) {
      loadAvailableLists(selectedTrustedProfile.id)
    } else {
      setAvailableLists([])
    }
  }, [selectedTrustedProfile])

  const loadMyLists = async () => {
    if (!activeProfile) return
    setLoadingMyLists(true)
    try {
      const data = await trustListService.getMyLists(activeProfile.id)
      setMyLists(data)
    } catch (err) {
      console.error('Failed to load lists:', err)
    } finally {
      setLoadingMyLists(false)
    }
  }

  const loadMyCategories = async () => {
    if (!activeProfile) return
    try {
      const data = await profileService.getTrustRelationships(activeProfile.id)
      setMyCategories(data)
    } catch (err) {
      console.error('Failed to load categories:', err)
    }
  }

  const loadTrustedProfiles = async () => {
    if (!activeProfile) return
    setLoadingTrustedProfiles(true)
    try {
      const data = await trustListService.getTrustedProfilesWithLists(activeProfile.id)
      setTrustedProfiles(data)
    } catch (err) {
      console.error('Failed to load trusted profiles:', err)
    } finally {
      setLoadingTrustedProfiles(false)
    }
  }

  const loadAvailableLists = async (trustedProfileId) => {
    setLoadingAvailableLists(true)
    try {
      const data = await trustListService.getAvailableLists(activeProfile.id, trustedProfileId)
      setAvailableLists(data)
    } catch (err) {
      console.error('Failed to load available lists:', err)
    } finally {
      setLoadingAvailableLists(false)
    }
  }

  // My Lists handlers
  const handleCreateList = async (e) => {
    e.preventDefault()
    if (!formData.name.trim()) {
      setError('List name is required')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      await trustListService.createList(activeProfile.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        is_public: formData.is_public,
        category_ids: formData.category_ids
      })

      setSuccess('List created successfully!')
      setShowCreateModal(false)
      resetFormData()
      loadMyLists()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create list')
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdateList = async (e) => {
    e.preventDefault()
    if (!editingList || !formData.name.trim()) return

    setSubmitting(true)
    setError(null)

    try {
      await trustListService.updateList(activeProfile.id, editingList.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        is_public: formData.is_public
      })

      setSuccess('List updated successfully!')
      setShowEditModal(false)
      setEditingList(null)
      resetFormData()
      loadMyLists()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update list')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteList = async (list) => {
    if (!confirm(`Are you sure you want to delete "${list.name}"?`)) return

    try {
      await trustListService.deleteList(activeProfile.id, list.id)
      setSuccess('List deleted successfully!')
      loadMyLists()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete list')
    }
  }

  const handleAddItems = async () => {
    if (!editingList || selectedCategoryIds.length === 0) return

    setSubmitting(true)
    setError(null)

    try {
      await trustListService.addItemsToList(activeProfile.id, editingList.id, selectedCategoryIds)
      setSuccess('Items added successfully!')
      setShowAddItemsModal(false)
      setSelectedCategoryIds([])
      loadMyLists()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add items')
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemoveItem = async (list, itemId) => {
    try {
      await trustListService.removeItemFromList(activeProfile.id, list.id, itemId)
      setSuccess('Item removed!')
      loadMyLists()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove item')
    }
  }

  // Adopt handlers
  const handleAdoptList = async () => {
    if (!adoptingList) return

    setSubmitting(true)
    setError(null)

    try {
      const data = {
        list_id: adoptingList.id,
        adopt_all: adoptOptions.adopt_all,
        duplicate_handling: adoptOptions.duplicate_handling
      }

      if (!adoptOptions.adopt_all && adoptOptions.item_ids.length > 0) {
        data.item_ids = adoptOptions.item_ids
      }

      if (adoptOptions.trust_override !== null) {
        data.trust_override = adoptOptions.trust_override
      }

      const result = await trustListService.adoptListItems(activeProfile.id, data)
      setSuccess(`Adopted ${result.adopted} items successfully!${result.skipped > 0 ? ` (${result.skipped} skipped)` : ''}`)
      setShowAdoptModal(false)
      setAdoptingList(null)
      resetAdoptOptions()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to adopt items')
    } finally {
      setSubmitting(false)
    }
  }

  // Helpers
  const resetFormData = () => {
    setFormData({
      name: '',
      description: '',
      is_public: true,
      category_ids: []
    })
  }

  const resetAdoptOptions = () => {
    setAdoptOptions({
      adopt_all: true,
      item_ids: [],
      trust_override: null,
      duplicate_handling: 'skip'
    })
  }

  const openEditModal = (list) => {
    setEditingList(list)
    setFormData({
      name: list.name,
      description: list.description || '',
      is_public: list.is_public,
      category_ids: []
    })
    setShowEditModal(true)
  }

  const openAddItemsModal = (list) => {
    setEditingList(list)
    setSelectedCategoryIds([])
    setShowAddItemsModal(true)
  }

  const openAdoptModal = (list) => {
    setAdoptingList(list)
    resetAdoptOptions()
    setShowAdoptModal(true)
  }

  const toggleCategorySelection = (categoryId) => {
    setSelectedCategoryIds(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId)
      } else {
        return [...prev, categoryId]
      }
    })
  }

  const toggleItemSelection = (itemId) => {
    setAdoptOptions(prev => {
      const newItemIds = prev.item_ids.includes(itemId)
        ? prev.item_ids.filter(id => id !== itemId)
        : [...prev.item_ids, itemId]
      return { ...prev, item_ids: newItemIds }
    })
  }

  // Get categories not already in the list
  const getAvailableCategories = (list) => {
    const existingIds = new Set(list.items.map(item => item.category_id))
    return myCategories.filter(cat => !existingIds.has(cat.id))
  }

  if (!activeProfile) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">Please select a profile to manage trust lists.</Alert>
      </Container>
    )
  }

  return (
    <Container className="mt-4">
      <h2>Trust Lists</h2>
      <p className="text-muted mb-4">
        Create shareable lists of trusted sources, or adopt lists from profiles you trust.
      </p>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      <Tabs defaultActiveKey="my-lists" className="mb-4">
        {/* My Lists Tab */}
        <Tab eventKey="my-lists" title="My Lists">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Lists you've created</h5>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              Create New List
            </Button>
          </div>

          {loadingMyLists ? (
            <div className="text-center py-4">
              <Spinner size="sm" className="me-2" />
              Loading lists...
            </div>
          ) : myLists.length === 0 ? (
            <Card className="text-center py-5">
              <Card.Body>
                <p className="text-muted mb-3">You haven't created any trust lists yet.</p>
                <p className="text-muted small mb-3">
                  Trust lists let you share curated sets of trusted sources with others who trust you.
                </p>
                <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                  Create Your First List
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <Accordion>
              {myLists.map((list, index) => (
                <Accordion.Item key={list.id} eventKey={String(index)}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100 me-3">
                      <span>
                        {list.name}
                        {!list.is_public && <Badge bg="secondary" className="ms-2">Private</Badge>}
                      </span>
                      <Badge bg="info">{list.items?.length || 0} items</Badge>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    {list.description && (
                      <p className="text-muted mb-3">{list.description}</p>
                    )}

                    <div className="mb-3">
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => openEditModal(list)}>
                        Edit
                      </Button>
                      <Button variant="outline-success" size="sm" className="me-2" onClick={() => openAddItemsModal(list)}>
                        Add Items
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDeleteList(list)}>
                        Delete
                      </Button>
                    </div>

                    {list.items && list.items.length > 0 ? (
                      <ListGroup>
                        {list.items.map(item => (
                          <ListGroup.Item key={item.id} className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{item.category_name}</strong>
                              {item.suggested_trust && (
                                <Badge bg="info" className="ms-2">Suggested: {item.suggested_trust}%</Badge>
                              )}
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveItem(list, item.id)}
                            >
                              Remove
                            </Button>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <Alert variant="info">No items in this list yet.</Alert>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </Tab>

        {/* Available Lists Tab */}
        <Tab eventKey="available-lists" title="Available Lists">
          <h5 className="mb-3">Lists from profiles you trust</h5>

          {loadingTrustedProfiles ? (
            <div className="text-center py-4">
              <Spinner size="sm" className="me-2" />
              Loading...
            </div>
          ) : trustedProfiles.length === 0 ? (
            <Alert variant="info">
              No trusted profiles with public lists available. Trust more profiles to see their shared lists.
            </Alert>
          ) : (
            <Row>
              <Col md={4}>
                <Card>
                  <Card.Header>Trusted Profiles</Card.Header>
                  <ListGroup variant="flush">
                    {trustedProfiles.map(profile => (
                      <ListGroup.Item
                        key={profile.id}
                        action
                        active={selectedTrustedProfile?.id === profile.id}
                        onClick={() => setSelectedTrustedProfile(profile)}
                      >
                        {profile.name}
                        <Badge bg="secondary" className="float-end">{profile.list_count} lists</Badge>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card>
              </Col>
              <Col md={8}>
                {selectedTrustedProfile ? (
                  loadingAvailableLists ? (
                    <div className="text-center py-4">
                      <Spinner size="sm" className="me-2" />
                      Loading lists...
                    </div>
                  ) : availableLists.length === 0 ? (
                    <Alert variant="info">No public lists from this profile.</Alert>
                  ) : (
                    <Accordion>
                      {availableLists.map((list, index) => (
                        <Accordion.Item key={list.id} eventKey={String(index)}>
                          <Accordion.Header>
                            <div className="d-flex justify-content-between align-items-center w-100 me-3">
                              <span>{list.name}</span>
                              <Badge bg="info">{list.items?.length || 0} items</Badge>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            {list.description && (
                              <p className="text-muted mb-3">{list.description}</p>
                            )}

                            <Button
                              variant="primary"
                              size="sm"
                              className="mb-3"
                              onClick={() => openAdoptModal(list)}
                            >
                              Adopt Items from this List
                            </Button>

                            {list.items && list.items.length > 0 ? (
                              <ListGroup>
                                {list.items.map(item => (
                                  <ListGroup.Item key={item.id}>
                                    <strong>{item.category_name}</strong>
                                    {item.suggested_trust && (
                                      <Badge bg="info" className="ms-2">Suggested: {item.suggested_trust}%</Badge>
                                    )}
                                  </ListGroup.Item>
                                ))}
                              </ListGroup>
                            ) : (
                              <Alert variant="info">No items in this list.</Alert>
                            )}
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  )
                ) : (
                  <Alert variant="secondary">Select a trusted profile to view their lists.</Alert>
                )}
              </Col>
            </Row>
          )}
        </Tab>
      </Tabs>

      {/* Create List Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Trust List</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateList}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>List Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Science Sources, News Sites"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="What kind of sources are in this list?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Make this list public (visible to profiles that trust you)"
                checked={formData.is_public}
                onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
              />
            </Form.Group>

            {myCategories.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>Add initial items (optional)</Form.Label>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {myCategories.map(cat => (
                    <Form.Check
                      key={cat.id}
                      type="checkbox"
                      label={cat.name}
                      checked={formData.category_ids.includes(cat.id)}
                      onChange={() => {
                        const newIds = formData.category_ids.includes(cat.id)
                          ? formData.category_ids.filter(id => id !== cat.id)
                          : [...formData.category_ids, cat.id]
                        setFormData({ ...formData, category_ids: newIds })
                      }}
                    />
                  ))}
                </div>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create List'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit List Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Trust List</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateList}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>List Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Make this list public"
                checked={formData.is_public}
                onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add Items Modal */}
      <Modal show={showAddItemsModal} onHide={() => setShowAddItemsModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Items to List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingList && getAvailableCategories(editingList).length === 0 ? (
            <Alert variant="info">All your trust relationships are already in this list.</Alert>
          ) : (
            <>
              <p className="text-muted">Select trust relationships to add:</p>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {editingList && getAvailableCategories(editingList).map(cat => (
                  <Form.Check
                    key={cat.id}
                    type="checkbox"
                    label={cat.name}
                    checked={selectedCategoryIds.includes(cat.id)}
                    onChange={() => toggleCategorySelection(cat.id)}
                  />
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddItemsModal(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleAddItems}
            disabled={submitting || selectedCategoryIds.length === 0}
          >
            {submitting ? 'Adding...' : `Add ${selectedCategoryIds.length} Item(s)`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Adopt Modal */}
      <Modal show={showAdoptModal} onHide={() => setShowAdoptModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Adopt Items from "{adoptingList?.name}"</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            Adopting items will create trust relationships in your profile based on this list.
          </Alert>

          <Form.Group className="mb-3">
            <Form.Check
              type="radio"
              label="Adopt all items"
              checked={adoptOptions.adopt_all}
              onChange={() => setAdoptOptions({ ...adoptOptions, adopt_all: true, item_ids: [] })}
            />
            <Form.Check
              type="radio"
              label="Select specific items"
              checked={!adoptOptions.adopt_all}
              onChange={() => setAdoptOptions({ ...adoptOptions, adopt_all: false })}
            />
          </Form.Group>

          {!adoptOptions.adopt_all && adoptingList?.items && (
            <Form.Group className="mb-3">
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {adoptingList.items.map(item => (
                  <Form.Check
                    key={item.id}
                    type="checkbox"
                    label={`${item.category_name}${item.suggested_trust ? ` (Suggested: ${item.suggested_trust}%)` : ''}`}
                    checked={adoptOptions.item_ids.includes(item.id)}
                    onChange={() => toggleItemSelection(item.id)}
                  />
                ))}
              </div>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Trust Override (optional)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              max="100"
              placeholder="Use suggested values"
              value={adoptOptions.trust_override || ''}
              onChange={(e) => setAdoptOptions({
                ...adoptOptions,
                trust_override: e.target.value ? parseInt(e.target.value) : null
              })}
            />
            <Form.Text className="text-muted">
              Leave blank to use each item's suggested trust value, or enter a value (0-100) to apply to all.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Duplicate Handling</Form.Label>
            <Form.Select
              value={adoptOptions.duplicate_handling}
              onChange={(e) => setAdoptOptions({ ...adoptOptions, duplicate_handling: e.target.value })}
            >
              <option value="skip">Skip existing relationships</option>
              <option value="update">Update existing relationships</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdoptModal(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleAdoptList}
            disabled={submitting || (!adoptOptions.adopt_all && adoptOptions.item_ids.length === 0)}
          >
            {submitting ? 'Adopting...' : 'Adopt Items'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default TrustLists
